import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/api/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Plus, IndianRupee, Truck, FileText, Eye, Edit2, Download, Trash2 } from "lucide-react";
import { useCanDelete } from "@/context/AuthContext";

const money = (v) => "₹" + Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const EMPTY = {
  supplier_id: "", supplier_name: "", depot_location: "",
  invoice_no: "", invoice_date: new Date().toISOString().slice(0, 10),
  bowser_id: "", driver_id: "", product: "HSD",
  quantity_litres: 0, rate_per_litre: 0, cgst: 0, sgst: 0, igst: 0, other_charges: 0,
  density_at_15c: 0.83, temperature_c: 30, seal_number: "", file_url: "", remarks: "",
};

/**
 * LoadingInvoices — CRUD page for depot-load invoices.
 *
 * Two entry points:
 *   * Manual "New" — captures a supplier-side invoice against a bowser fill.
 *   * Auto-generated ones flow in from the procurement release step (source =
 *     `procurement_release`) and can no longer be edited freely — we still allow
 *     rate/tax + payment-status edits but the qty stays immutable.
 *
 * Each row exposes 4 actions: View · Edit · Download PDF · Delete.
 */
export default function LoadingInvoices() {
  const qc = useQueryClient();
  const canDelete = useCanDelete();
  const { data } = useQuery({
    queryKey: ["loading-invoices"],
    queryFn: () => api.get("/loading-invoices").then((r) => r.data),
  });
  const bowsers = useQuery({
    queryKey: ["fleet-bowsers-li"],
    queryFn: () => api.get("/fleet/bowsers").then((r) => r.data),
  }).data?.items || [];
  const suppliers = useQuery({
    queryKey: ["fleet-suppliers-li"],
    queryFn: () => api.get("/procurement/suppliers").then((r) => r.data).catch(() => ({ items: [] })),
  }).data?.items || [];

  // ─── create ───
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const subtotal = Number(((form.quantity_litres || 0) * (form.rate_per_litre || 0)).toFixed(2));
  const total = Number((subtotal + Number(form.cgst || 0) + Number(form.sgst || 0) + Number(form.igst || 0) + Number(form.other_charges || 0)).toFixed(2));
  const create = useMutation({
    mutationFn: () => api.post("/loading-invoices", form),
    onSuccess: () => {
      toast.success("Loading invoice saved & bowser stock updated");
      qc.invalidateQueries({ queryKey: ["loading-invoices"] });
      qc.invalidateQueries({ queryKey: ["fleet-bowsers-li"] });
      setOpen(false); setForm(EMPTY);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Save failed"),
  });

  // ─── view + edit ───
  const [viewLi, setViewLi] = useState(null);
  const [editLi, setEditLi] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const editSubtotal = editForm ? Number(((editForm.quantity_litres || 0) * (editForm.rate_per_litre || 0)).toFixed(2)) : 0;
  const editTotal = editForm ? Number((editSubtotal + Number(editForm.cgst || 0) + Number(editForm.sgst || 0) + Number(editForm.igst || 0) + Number(editForm.other_charges || 0)).toFixed(2)) : 0;

  const patch = useMutation({
    mutationFn: (payload) => api.patch(`/loading-invoices/${editLi.id}`, payload),
    onSuccess: () => {
      toast.success("Loading invoice updated");
      setEditLi(null); setEditForm(null);
      qc.invalidateQueries({ queryKey: ["loading-invoices"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Update failed"),
  });

  const markPaid = useMutation({
    mutationFn: ({ id, status }) => api.patch(`/loading-invoices/${id}/payment?status=${status}`),
    onSuccess: () => { toast.success("Payment updated"); qc.invalidateQueries({ queryKey: ["loading-invoices"] }); },
  });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/loading-invoices/${id}`),
    onSuccess: () => { toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["loading-invoices"] }); },
  });

  const downloadPdf = (li) => {
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    window.open(`${base}/api/loading-invoices/${li.id}/pdf`, "_blank", "noopener");
  };

  const items = data?.items || [];

  return (
    <div className="space-y-4" data-testid="loading-invoices-page">
      <div>
        <h1 className="font-display text-3xl font-black tracking-tight">Loading Invoices</h1>
        <p className="text-sm text-muted-foreground">
          Auto-generated on every procurement release (bowser or tank fill), plus manual depot loads.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <Kpi icon={FileText} label="Invoices" value={data?.count || 0} />
        <Kpi icon={Truck} label="Total loaded" value={`${(data?.total_litres || 0).toLocaleString("en-IN")} L`} />
        <Kpi icon={IndianRupee} label="Total cost" value={money(data?.total_cost)} />
      </div>

      <Card>
        <CardHeader className="flex-row items-center justify-between border-b border-border/60 py-3">
          <CardTitle className="text-sm uppercase tracking-widest text-muted-foreground">All loading invoices</CardTitle>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="btn-new-loading-invoice"><Plus size={14} className="mr-1" /> New</Button></DialogTrigger>
            <DialogContent className="max-w-3xl">
              <DialogHeader>
                <DialogTitle>Record depot loading</DialogTitle>
                <DialogDescription>Manual entry — for procurement-released loads the invoice is created automatically.</DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
                <div className="sm:col-span-2 md:col-span-2">
                  <Label>Supplier</Label>
                  <Select value={form.supplier_id || "__none__"} onValueChange={(v) => setForm({ ...form, supplier_id: v === "__none__" ? "" : v, supplier_name: suppliers.find((s) => s.id === v)?.name || "" })}>
                    <SelectTrigger data-testid="li-supplier"><SelectValue placeholder="Choose supplier" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— None —</SelectItem>
                      {suppliers.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Depot location</Label><Input value={form.depot_location} onChange={(e) => setForm({ ...form, depot_location: e.target.value })} placeholder="IOCL Pune" /></div>
                <div><Label>Supplier invoice # <span className="text-status-cancelled">*</span></Label><Input value={form.invoice_no} onChange={(e) => setForm({ ...form, invoice_no: e.target.value })} data-testid="li-invoice-no" /></div>
                <div><Label>Invoice date</Label><Input type="date" value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} /></div>
                <div>
                  <Label>Bowser <span className="text-status-cancelled">*</span></Label>
                  <Select value={form.bowser_id} onValueChange={(v) => setForm({ ...form, bowser_id: v })}>
                    <SelectTrigger data-testid="li-bowser"><SelectValue placeholder="Select bowser" /></SelectTrigger>
                    <SelectContent>
                      {bowsers.map((b) => (<SelectItem key={b.id} value={b.id}>{b.code} — {b.registration_no} ({b.current_stock?.toFixed(0)}/{b.capacity_litres}L)</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Quantity (L) <span className="text-status-cancelled">*</span></Label><Input type="number" step="0.01" value={form.quantity_litres || ""} onChange={(e) => setForm({ ...form, quantity_litres: Number(e.target.value) })} data-testid="li-qty" /></div>
                <div><Label>Rate (₹/L) <span className="text-status-cancelled">*</span></Label><Input type="number" step="0.01" value={form.rate_per_litre || ""} onChange={(e) => setForm({ ...form, rate_per_litre: Number(e.target.value) })} data-testid="li-rate" /></div>
                <div><Label>Subtotal</Label><Input value={money(subtotal)} disabled /></div>
                <div><Label>CGST (₹)</Label><Input type="number" step="0.01" value={form.cgst || ""} onChange={(e) => setForm({ ...form, cgst: Number(e.target.value) })} /></div>
                <div><Label>SGST (₹)</Label><Input type="number" step="0.01" value={form.sgst || ""} onChange={(e) => setForm({ ...form, sgst: Number(e.target.value) })} /></div>
                <div><Label>IGST (₹)</Label><Input type="number" step="0.01" value={form.igst || ""} onChange={(e) => setForm({ ...form, igst: Number(e.target.value) })} /></div>
                <div><Label>Density @ 15°C</Label><Input type="number" step="0.001" value={form.density_at_15c || ""} onChange={(e) => setForm({ ...form, density_at_15c: Number(e.target.value) })} /></div>
                <div><Label>Temperature (°C)</Label><Input type="number" step="0.1" value={form.temperature_c || ""} onChange={(e) => setForm({ ...form, temperature_c: Number(e.target.value) })} /></div>
                <div><Label>Seal number</Label><Input value={form.seal_number} onChange={(e) => setForm({ ...form, seal_number: e.target.value })} /></div>
                <div className="col-span-3 rounded-md border-2 border-primary/30 bg-primary/5 p-3">
                  <div className="text-xs text-muted-foreground">Total to pay</div>
                  <div className="mt-1 font-display text-2xl font-black text-primary" data-testid="li-total">{money(total)}</div>
                </div>
                <div className="sm:col-span-2 md:col-span-3"><Label>Remarks</Label><Textarea rows={2} value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></div>
              </div>
              <DialogFooter>
                <Button
                  onClick={() => create.mutate()}
                  disabled={!form.invoice_no || !form.bowser_id || form.quantity_litres <= 0 || create.isPending}
                  data-testid="btn-save-loading-invoice"
                >
                  {create.isPending ? "Saving…" : "Save & Update Stock"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="data-table w-full">
              <thead className="border-b border-border/60 bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Code</th>
                  <th className="px-4 py-3">Supplier Inv</th>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Supplier</th>
                  <th className="px-4 py-3">Destination</th>
                  <th className="px-4 py-3 text-right">Qty (L)</th>
                  <th className="px-4 py-3 text-right">Total</th>
                  <th className="px-4 py-3">Source</th>
                  <th className="px-4 py-3">Payment</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((l) => {
                  const destCode = l.dest_code || l.bowser_code || "—";
                  const destKind = (l.dest_kind || "bowser").toUpperCase();
                  return (
                    <tr key={l.id} className="border-b border-border/40 text-sm" data-testid={`li-row-${l.code}`}>
                      <td className="px-4 py-3 font-mono font-semibold">{l.code}</td>
                      <td className="px-4 py-3 font-mono text-xs">{l.invoice_no || "—"}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-xs">{(l.invoice_date || "").slice(0, 10)}</td>
                      <td className="px-4 py-3 text-xs">{l.supplier_name || "—"}</td>
                      <td className="px-4 py-3">
                        <div className="font-mono text-xs font-semibold">{destCode}</div>
                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{destKind}</div>
                      </td>
                      <td className="px-4 py-3 text-right font-mono">{Number(l.quantity_litres).toLocaleString("en-IN")}</td>
                      <td className="px-4 py-3 text-right font-mono font-bold">{money(l.total)}</td>
                      <td className="px-4 py-3">
                        {l.source === "procurement_release"
                          ? <Badge className="border-blue-300 bg-blue-100 text-[10px] text-blue-700">Auto</Badge>
                          : <Badge variant="outline" className="text-[10px]">Manual</Badge>}
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={
                          l.payment_status === "paid" ? "bg-status-delivered/15 text-status-delivered" :
                          l.payment_status === "partial" ? "bg-status-pending/15 text-status-pending" :
                          "bg-status-cancelled/15 text-status-cancelled"
                        }>{l.payment_status}</Badge>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-1">
                          <Button size="icon" variant="ghost" onClick={() => setViewLi(l)} data-testid={`li-view-${l.code}`} title="View">
                            <Eye size={14} />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => { setEditLi(l); setEditForm({ ...l }); }}
                            data-testid={`li-edit-${l.code}`}
                            title="Edit"
                          >
                            <Edit2 size={14} />
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => downloadPdf(l)} data-testid={`li-download-${l.code}`} title="Download PDF">
                            <Download size={14} />
                          </Button>
                          {l.payment_status !== "paid" && (
                            <Button size="sm" variant="outline" onClick={() => markPaid.mutate({ id: l.id, status: "paid" })} data-testid={`li-pay-${l.code}`}>
                              Mark paid
                            </Button>
                          )}
                          {canDelete && (
                            <Button size="icon" variant="ghost" onClick={() => del.mutate(l.id)} data-testid={`li-del-${l.code}`} title="Delete">
                              <Trash2 size={14} className="text-red-600" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {items.length === 0 && (
                  <tr>
                    <td colSpan={10} className="px-4 py-10 text-center text-sm text-muted-foreground">
                      <FileText size={32} className="mx-auto mb-2 opacity-40" />
                      No loading invoices yet. Record your first depot pickup.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* ────────── View dialog ────────── */}
      <Dialog open={!!viewLi} onOpenChange={(v) => !v && setViewLi(null)}>
        <DialogContent className="sm:max-w-2xl" data-testid="li-view-dialog">
          <DialogHeader>
            <DialogTitle>Loading Invoice · {viewLi?.code}</DialogTitle>
            <DialogDescription>
              {viewLi?.source === "procurement_release" ? "Auto-generated on procurement release" : "Manually recorded"}
            </DialogDescription>
          </DialogHeader>
          {viewLi && (
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <Field label="Supplier" value={viewLi.supplier_name || "—"} />
              <Field label="Supplier invoice #" value={viewLi.invoice_no || "—"} mono />
              <Field label="Depot location" value={viewLi.depot_location || "—"} />
              <Field label="Invoice date" value={(viewLi.invoice_date || "").slice(0, 10)} />
              <Field label="Destination" value={`${(viewLi.dest_kind || "bowser").toUpperCase()} · ${viewLi.dest_code || viewLi.bowser_code || "—"}`} mono />
              <Field label="Product" value={viewLi.product} />
              <Field label="Quantity" value={`${Number(viewLi.quantity_litres).toLocaleString("en-IN")} L`} mono />
              <Field label="Rate / L" value={money(viewLi.rate_per_litre)} mono />
              <Field label="Subtotal" value={money(viewLi.subtotal)} mono />
              <Field label="CGST" value={money(viewLi.cgst)} mono />
              <Field label="SGST" value={money(viewLi.sgst)} mono />
              <Field label="IGST" value={money(viewLi.igst)} mono />
              <Field label="Other charges" value={money(viewLi.other_charges)} mono />
              <Field label="TOTAL" value={money(viewLi.total)} mono bold />
              <Field label="Density @ 15°C" value={`${viewLi.density_at_15c || "—"} kg/L`} />
              <Field label="Temperature" value={`${viewLi.temperature_c || "—"} °C`} />
              <Field label="Seal number" value={viewLi.seal_number || "—"} mono />
              <Field label="Payment status" value={<Badge>{viewLi.payment_status}</Badge>} />
              {viewLi.po_code && <Field label="Linked PO" value={viewLi.po_code} mono />}
              {viewLi.batch && <Field label="Batch" value={viewLi.batch} mono />}
              {viewLi.remarks && <div className="col-span-2 mt-2 rounded-md border border-border/60 bg-muted/30 p-2 text-xs">{viewLi.remarks}</div>}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => downloadPdf(viewLi)} data-testid="li-view-download">
              <Download size={14} className="mr-1" /> Download PDF
            </Button>
            <Button onClick={() => setViewLi(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ────────── Edit dialog ────────── */}
      <Dialog open={!!editLi} onOpenChange={(v) => !v && (setEditLi(null), setEditForm(null))}>
        <DialogContent className="max-w-3xl" data-testid="li-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit · {editLi?.code}</DialogTitle>
            <DialogDescription>
              Quantity is locked (would require reversing stock movements). Rate + tax + payment status can be edited.
            </DialogDescription>
          </DialogHeader>
          {editForm && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
              <div className="sm:col-span-2 md:col-span-2"><Label>Supplier</Label><Input value={editForm.supplier_name || ""} onChange={(e) => setEditForm({ ...editForm, supplier_name: e.target.value })} /></div>
              <div><Label>Depot location</Label><Input value={editForm.depot_location || ""} onChange={(e) => setEditForm({ ...editForm, depot_location: e.target.value })} /></div>
              <div><Label>Supplier invoice #</Label><Input value={editForm.invoice_no || ""} onChange={(e) => setEditForm({ ...editForm, invoice_no: e.target.value })} /></div>
              <div><Label>Invoice date</Label><Input type="date" value={(editForm.invoice_date || "").slice(0, 10)} onChange={(e) => setEditForm({ ...editForm, invoice_date: e.target.value })} /></div>
              <div>
                <Label>Payment status</Label>
                <Select value={editForm.payment_status || "unpaid"} onValueChange={(v) => setEditForm({ ...editForm, payment_status: v })}>
                  <SelectTrigger data-testid="li-edit-payment"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unpaid">Unpaid</SelectItem>
                    <SelectItem value="partial">Partial</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Qty (locked)</Label><Input value={Number(editForm.quantity_litres).toLocaleString("en-IN") + " L"} disabled /></div>
              <div><Label>Rate (₹/L)</Label><Input type="number" step="0.01" value={editForm.rate_per_litre || 0} onChange={(e) => setEditForm({ ...editForm, rate_per_litre: Number(e.target.value) })} data-testid="li-edit-rate" /></div>
              <div><Label>Subtotal</Label><Input value={money(editSubtotal)} disabled /></div>
              <div><Label>CGST (₹)</Label><Input type="number" step="0.01" value={editForm.cgst || 0} onChange={(e) => setEditForm({ ...editForm, cgst: Number(e.target.value) })} /></div>
              <div><Label>SGST (₹)</Label><Input type="number" step="0.01" value={editForm.sgst || 0} onChange={(e) => setEditForm({ ...editForm, sgst: Number(e.target.value) })} /></div>
              <div><Label>IGST (₹)</Label><Input type="number" step="0.01" value={editForm.igst || 0} onChange={(e) => setEditForm({ ...editForm, igst: Number(e.target.value) })} /></div>
              <div><Label>Other charges (₹)</Label><Input type="number" step="0.01" value={editForm.other_charges || 0} onChange={(e) => setEditForm({ ...editForm, other_charges: Number(e.target.value) })} /></div>
              <div><Label>Density @ 15°C</Label><Input type="number" step="0.001" value={editForm.density_at_15c || 0} onChange={(e) => setEditForm({ ...editForm, density_at_15c: Number(e.target.value) })} /></div>
              <div><Label>Temperature (°C)</Label><Input type="number" step="0.1" value={editForm.temperature_c || 0} onChange={(e) => setEditForm({ ...editForm, temperature_c: Number(e.target.value) })} /></div>
              <div><Label>Seal number</Label><Input value={editForm.seal_number || ""} onChange={(e) => setEditForm({ ...editForm, seal_number: e.target.value })} /></div>
              <div className="col-span-3 rounded-md border-2 border-primary/30 bg-primary/5 p-3">
                <div className="text-xs text-muted-foreground">New total</div>
                <div className="mt-1 font-display text-2xl font-black text-primary">{money(editTotal)}</div>
              </div>
              <div className="sm:col-span-2 md:col-span-3"><Label>Remarks</Label><Textarea rows={2} value={editForm.remarks || ""} onChange={(e) => setEditForm({ ...editForm, remarks: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditLi(null); setEditForm(null); }}>Cancel</Button>
            <Button
              onClick={() => {
                // Send only the editable fields (matches the LoadingInvoiceUpdate model on backend)
                const payload = {
                  supplier_name: editForm.supplier_name,
                  depot_location: editForm.depot_location,
                  invoice_no: editForm.invoice_no,
                  invoice_date: editForm.invoice_date,
                  rate_per_litre: editForm.rate_per_litre,
                  cgst: editForm.cgst, sgst: editForm.sgst, igst: editForm.igst,
                  other_charges: editForm.other_charges,
                  density_at_15c: editForm.density_at_15c,
                  temperature_c: editForm.temperature_c,
                  seal_number: editForm.seal_number,
                  remarks: editForm.remarks,
                  payment_status: editForm.payment_status,
                };
                patch.mutate(payload);
              }}
              disabled={patch.isPending}
              data-testid="btn-save-li-edit"
            >
              {patch.isPending ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value, mono, bold }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`${mono ? "font-mono" : ""} ${bold ? "text-lg font-bold" : ""}`}>{value}</div>
    </div>
  );
}

const Kpi = ({ icon: Icon, label, value }) => (
  <div className="kpi-card">
    <div className="flex items-center justify-between text-muted-foreground">
      <div className="text-xs uppercase tracking-widest">{label}</div>
      <Icon size={14} />
    </div>
    <div className="mt-2 font-display text-xl font-black">{value}</div>
  </div>
);
